import React from 'react';
import FormNumberInput from '@/components/UI/FormNumberInput';
import FormSelect from '@/components/UI/FormSelect';
import FormSwitch from '@/components/UI/FormSwitch';
import { Card } from '@/components/UI';
import type {
    SessionCloseDetectionCutoff,
    SessionCloseExecutionPolicy,
    SessionTimingPolicy,
    SessionTimingPolicyByDuration
} from '@einhaus/project314-types';
import type { TradingStrategyFormData } from '../tradingStrategyEditorSubmitPayloads';

interface SessionTimingPolicyCardProps {
    sessionTimingPolicy: TradingStrategyFormData['sessionTimingPolicy'];
    setFormData: React.Dispatch<React.SetStateAction<TradingStrategyFormData>>;
    validationErrors: Record<string, string>;
}

const getDefaultSessionTimingPolicy = (duration: keyof SessionTimingPolicyByDuration): SessionTimingPolicy => {
    if (duration === '30m' || duration === '240m') {
        return {
            detectionCutoff: 'nearClose',
            executionPolicy: 'nearClose',
            preCloseOffsetSecs: 60
        };
    }

    if (duration === '1d') {
        return {
            detectionCutoff: 'nearClose',
            executionPolicy: 'nearClose',
            preCloseOffsetSecs: 120
        };
    }

    if (duration === '1w' || duration === '1mo') {
        return { detectionCutoff: 'close', executionPolicy: 'nextOpen' };
    }

    return { detectionCutoff: 'close', executionPolicy: 'atClosePlus1s' };
};

const SessionTimingPolicyCard = ({ sessionTimingPolicy, setFormData, validationErrors }: SessionTimingPolicyCardProps) => (
    <Card
        subtitle="Platform defaults apply automatically. Add an override only when this strategy needs different session-close timing."
        title="Session timing"
    >
        {(['1m', '3m', '5m', '15m', '30m', '60m', '120m', '180m', '240m', '1d', '1w', '1mo'] as const).map((dur) => {
            const policy = sessionTimingPolicy?.[dur];
            const enabled = !!policy;
            const isMinuteDuration = dur.endsWith('m') && dur !== '1mo';
            const defaultPreCloseOffsetSecs = isMinuteDuration ? 60 : 120;
            const platformDefault = getDefaultSessionTimingPolicy(dur);
            const usesAutomaticPreCloseDefault = dur === '30m' || dur === '240m';
            return (
                <div className="mb-4 rounded-lg border border-zinc-700 p-4 last:mb-0" key={dur}>
                    <div className="mb-3 flex items-center justify-between">
                        <div className="font-medium text-white">{dur.toUpperCase()} policy</div>
                        <FormSwitch
                            checked={enabled}
                            label={enabled ? 'Custom override' : 'Using default'}
                            onChange={(e) => {
                                const isOn = e.target.checked;

                                setFormData((prev) => {
                                    const next: TradingStrategyFormData = { ...prev };
                                    const current = prev.sessionTimingPolicy ? { ...prev.sessionTimingPolicy } : {};

                                    if (isOn) {
                                        current[dur] = platformDefault;
                                    } else {
                                        delete current[dur];
                                    }

                                    next.sessionTimingPolicy = Object.keys(current).length ? current : null;
                                    return next;
                                });
                            }}
                        />
                    </div>
                    {!enabled && usesAutomaticPreCloseDefault && (
                        <p className="text-xs text-zinc-400">
                            Automatic default: the session-close bar detects and executes 60 seconds before the actual session close.
                        </p>
                    )}
                    {enabled && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <FormSelect
                                label="Detection cutoff"
                                options={[
                                    { value: 'close', label: 'Close' },
                                    { value: 'nearClose', label: 'Near close' },
                                    { value: 'priorClose', label: 'Prior close' }
                                ]}
                                value={policy?.detectionCutoff || ''}
                                onChange={(e) => {
                                    const value = e.target.value as SessionCloseDetectionCutoff;

                                    setFormData((prev) => {
                                        const next: TradingStrategyFormData = { ...prev };
                                        const dp: SessionTimingPolicyByDuration = { ...(prev.sessionTimingPolicy || {}) };

                                        const existing: SessionTimingPolicy = dp[dur] ?? platformDefault;

                                        const updated: SessionTimingPolicy = {
                                            ...existing,
                                            detectionCutoff: value
                                        };

                                        // Auto-default offset when switching to nearClose
                                        if (
                                            value === 'nearClose' &&
                                            (updated.preCloseOffsetSecs === undefined || updated.preCloseOffsetSecs === null)
                                        ) {
                                            updated.preCloseOffsetSecs = defaultPreCloseOffsetSecs;
                                        }

                                        dp[dur] = updated;
                                        next.sessionTimingPolicy = dp;
                                        return next;
                                    });
                                }}
                            />
                            <FormSelect
                                label="Execution policy"
                                options={[
                                    { value: 'atClosePlus1s', label: 'At close + 1s' },
                                    { value: 'nearClose', label: 'Near close' },
                                    { value: 'nextOpen', label: 'Next open' }
                                ]}
                                value={policy?.executionPolicy || ''}
                                onChange={(e) => {
                                    const value = e.target.value as SessionCloseExecutionPolicy;

                                    setFormData((prev) => {
                                        const next: TradingStrategyFormData = { ...prev };
                                        const dp: SessionTimingPolicyByDuration = { ...(prev.sessionTimingPolicy || {}) };

                                        const existing: SessionTimingPolicy = dp[dur] ?? platformDefault;

                                        const updated: SessionTimingPolicy = {
                                            ...existing,
                                            executionPolicy: value
                                        };

                                        // Auto-default offset when switching to nearClose
                                        if (
                                            value === 'nearClose' &&
                                            (updated.preCloseOffsetSecs === undefined || updated.preCloseOffsetSecs === null)
                                        ) {
                                            updated.preCloseOffsetSecs = defaultPreCloseOffsetSecs;
                                        }

                                        dp[dur] = updated;
                                        next.sessionTimingPolicy = dp;
                                        return next;
                                    });
                                }}
                            />
                            <FormNumberInput
                                integer
                                error={validationErrors[`sessionTimingPolicy_${dur}_preCloseOffsetSecs`]}
                                label="Pre-close offset (secs)"
                                max={120}
                                min={0}
                                placeholder="e.g., 120"
                                value={policy?.preCloseOffsetSecs}
                                onValueChange={(parsed) => {
                                    const num = parsed ?? undefined;

                                    setFormData((prev) => {
                                        const next: TradingStrategyFormData = { ...prev };
                                        const dp: SessionTimingPolicyByDuration = { ...(prev.sessionTimingPolicy || {}) };

                                        const base: SessionTimingPolicy = dp[dur] ?? platformDefault;

                                        dp[dur] = { ...base, preCloseOffsetSecs: num };
                                        next.sessionTimingPolicy = dp;
                                        return next;
                                    });
                                }}
                            />
                        </div>
                    )}
                </div>
            );
        })}
    </Card>
);

export default SessionTimingPolicyCard;
